import {
    Button,
    DialogHandler,
    fetchRoute,
    GenericWrapperContext,
    getBrandUrl,
    Icon,
    LoadingContainer,
    Modal,
    SubNav,
    TemplateModel,
    TemplatesFactory,
    TNumStr,
    TTemplateSharePayload
} from "@cakemail-org/ui-components-v2"
import { MouseEvent, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom"
import { ETemplateSubNav, TTemplateDetails, TUpdateTemplate } from "../types"
import { useTranslation } from "react-i18next"
import classNames from "classnames"
import { TemplateDetailContainerContext } from "../context"
import { ERoutes } from "../../../utils/url"
import { getBaseBeeTemplate } from "../../../data/bee"
import { TemplatePreview } from "../components/preview"
import { TemplateMainDetails } from "../components/mainDetails"
import Sharing from "../components/sharing"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { TSubNavItem } from "@cakemail-org/ui-components-v2/dist/esm/components/subNav/types"


export default function TemplateDetails({ initTab, isNewAccount }: TTemplateDetails) {
    const genericWrapperContext = useContext(GenericWrapperContext)
    const navigate = useNavigate()
    const params = useParams()

    const { t, i18n } = useTranslation()

    const [showPreview, setShowPreview] = useState<boolean>(false)
    const [template, setTemplate] = useState<TemplateModel>()
    const [nav, setNav] = useState<ETemplateSubNav>(initTab || ETemplateSubNav.details)
    const templateRef = useRef<TemplateModel>(new TemplateModel({}))

    const defaultName = useRef(t("general-untitled"))
    const isNew = isNewAccount || params.templateId === "new"
    const isPublished = template?.sharing.shared

    useEffect(getTemplate, [])

    function getTemplate(fresh: boolean = false) {
        if (isNew && !fresh) {
            setTemplate(new TemplateModel({}))
            templateRef.current = new TemplateModel({})
        } else {
            fetchTemplate()
        }
    }

    async function fetchTemplate({ btn }: { btn?: TButtonRef } = {}) {
        return TemplatesFactory.get({ id: String(params.templateId) }).then((a) => {
            setTemplate(new TemplateModel(a))
            templateRef.current = a
            btn && btn.reset()
        }).catch(({ msg }) => {
            navigate(fetchRoute({ route: ERoutes.templates }))
            DialogHandler.show({
                title: t(msg || "general-error-msg")
            })
        })
    }


    function handleChangeSubNav(event: MouseEvent<HTMLButtonElement>) {
        const { name } = event.currentTarget
        setNav(name as ETemplateSubNav)
    }

    function handlePreview() {
        setShowPreview(!showPreview)
    }

    function updateTemplate({ data, btnRef, goToEdit, noConfirmation }: TUpdateTemplate): Promise<void> | void {
        if (!template) return

        if (isNew) {

            return TemplatesFactory.create({
                template: {
                    name: template.name || defaultName.current,
                    description: template.description || undefined,
                    tags: ["free", "sys_lang_en_us", ...template.tags, "bee"],
                    //@ts-expect-error
                    content: {
                        type: "bee",
                        json: getBaseBeeTemplate()
                    }
                }
            })
                .then((updatedTemplate) => {
                    if (goToEdit) {
                        handleEdit(updatedTemplate.id)
                        return
                    }
                    navigate(fetchRoute({ route: ERoutes.templateDetails, params: { templateId: updatedTemplate.id } }))
                    setTemplate(new TemplateModel(updatedTemplate))
                    templateRef.current = new TemplateModel(updatedTemplate)
                    btnRef && btnRef.reset()
                    if (!noConfirmation) {
                        DialogHandler.show({
                            type: "success",
                            title: t("template-detail-updated-txt")
                        })
                    }
                    return
                })
        }

        return template.update(data || {})
            .then((updatedTemplate) => {
                setTemplate(new TemplateModel(updatedTemplate.data))
                templateRef.current = new TemplateModel(updatedTemplate.data)
                btnRef && btnRef.reset()
                if (!noConfirmation) {
                    DialogHandler.show({
                        type: "success",
                        title: t("template-detail-updated-txt")
                    })
                }
                return
            }).catch(({ msg }) => {
                btnRef && btnRef.reset()
                DialogHandler.show({
                    type: "warning",
                    title: t(msg || "template-detail-updated-error")
                })
            })
    }

    function handleEdit(templateId?: TNumStr) {
        window.location.href = "#" + fetchRoute({ route: ERoutes.templateEdit, params: { templateId: templateId || params?.templateId } })
    }

    async function handleShare({ accounts, automatic, everyone, btn }: TTemplateSharePayload & { btn?: TButtonRef } = {}) {
        return template?.share({ automatic, everyone, accounts }).then(() => {
            return fetchTemplate({ btn })
        }).catch(({ msg, error }) => {
            DialogHandler.show({
                type: "warning",
                title: t(msg || "template-share-error")
            })
            btn && btn.reset()
            throw error
        })
    }

    async function handleUnshare({ btn }: { btn?: TButtonRef }) {
        return template?.unshare().then(() => {
            return fetchTemplate({ btn })
        }).catch(({ msg, error }) => {
            DialogHandler.show({
                type: "warning",
                title: t(msg || "template-unshare-error")
            })
            btn && btn.reset()
            throw error
        })
    }

    let isLoading = false
    const isOwner = template?.is_owner
    if (!template) {
        isLoading = true
    }

    const modalClasses = classNames("assetManagerModal", "templateModal", {
        "isPreview": showPreview
    })

    const subNavItems = useMemo(() => {
        const subNavItems: TSubNavItem[] = [
            { name: ETemplateSubNav.details, text: t("general-details") },
            {
                name: ETemplateSubNav.edit, text: t(isNew ? "general-create-template" : "assets-sub-nav-edit"), onClick: () => {
                    if (isNew) {
                        updateTemplate({ goToEdit: true })
                    } else {
                        handleEdit()
                    }
                },
                disabled: !isOwner && !isNew
            }
        ]
        if (genericWrapperContext.account?.isPartnerOrOrganization()) {
            subNavItems.push({
                name: ETemplateSubNav.sharing,
                text: t("assets-sub-nav-sharing"),
                disabled: isNew
            })
        }

        return subNavItems
    }, [isNew, isOwner])

    return <Modal
        open
        className={modalClasses}
        heading={{
            logoUrl: getBrandUrl({
                elKey: "logo",
                brand: genericWrapperContext?.partnerBrand,
                locale: genericWrapperContext?.user?.language
            }),
            titleContent: [<SubNav
                key="subNav"
                menuVariant="text"
                onClick={handleChangeSubNav}
                initialSelected={nav}
                items={subNavItems}
            />],
            actions: [
                <Button
                    key="button1"
                    disabled={!template}
                    onClick={handlePreview}
                    startIcon={<Icon name={showPreview ? "X18" : "Preview18"} />}
                    variant="outlined"
                    color="secondary"
                >
                    {t("general-preview")}
                </Button>,

                (
                    isPublished ?
                        <Button
                            key="button3"
                            disabled={!template || !isOwner}
                            animated
                            onClick={(e, btn) => { handleUnshare({ btn }) }}
                            startIcon={<Icon name={"Publish18"} />}
                            variant="contained"
                            color={!isOwner ? "cloud" : "primary"}
                        >
                            {t(!isOwner ? "general-shared" : "general-unpublish")}
                        </Button>
                        :
                        <Button
                            key="button2"
                            animated
                            disabled={!template || isNew || !isOwner}
                            onClick={(e, btn) => { handleShare({ btn }) }}
                            startIcon={<Icon name={"Publish18"} />}
                            variant="contained"
                            color={!isOwner ? "cloud" : "primary"}
                        >
                            {t(!isOwner ? "general-unshared" : "general-publish")}
                        </Button>
                )
            ],
            closeBtnOnClick: () => { navigate(fetchRoute({ route: ERoutes.templates })) },
            closeBtnText: t("general-close")
        }}
        content={
            <LoadingContainer loading={isLoading}>
                {template && templateRef &&
                    <TemplateDetailContainerContext.Provider value={{
                        template,
                        templateRef,
                        setTemplate,
                        updateTemplate,
                        handleShare,
                        handleUnshare
                    }}>
                        {(showPreview) && <TemplatePreview />}

                        {!showPreview && nav === ETemplateSubNav.details && <TemplateMainDetails isNew={isNew} />}
                        {!showPreview && nav === ETemplateSubNav.sharing && <Sharing />}
                    </TemplateDetailContainerContext.Provider>
                }

            </LoadingContainer>
        }
    />

}
